import React, { useRef } from 'react';
import {
  IconCalendarEvent,
  IconCheck,
  IconClipboardCheck,
  IconClipboardX,
  IconFileDescription,
  IconId,
  IconPlaneArrival,
  IconTicket,
} from '@tabler/icons-react';
import { Lead } from '../model/types';
import type { DocType } from '../model/types';
import { formatDate, getManagerInitials, getManagerColor } from '../../../shared/lib/utils';
import { formatTripLeg } from '../../../shared/lib/tripTime';

const DOC_ICONS: { type: DocType; label: string; Icon: typeof IconId }[] = [
  { type: 'passport', label: 'Паспорт', Icon: IconId },
  { type: 'ticket', label: 'Билет', Icon: IconTicket },
  { type: 'attitude', label: 'Отношение', Icon: IconFileDescription },
];

const presentDocTypes = (types: Lead['types']): Set<DocType> => {
  if (!types) return new Set();
  let parsed: unknown = types;
  if (typeof parsed === 'string') {
    try { parsed = JSON.parse(parsed); } catch { return new Set(); }
  }
  if (!parsed || typeof parsed !== 'object') return new Set();
  const values = Array.isArray(parsed) ? parsed : Object.values(parsed);
  return new Set(values.filter((v): v is DocType =>
    v === 'passport' || v === 'ticket' || v === 'attitude'));
};

interface LeadCardProps {
  lead: Lead;
  onClick: (id: string, title?: string) => void;
  isDragging?: boolean;
  onDragStart?: (leadId: string) => void;
  onDragEnd?: () => void;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (leadId: string) => void;
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  onClick,
  isDragging = false,
  onDragStart,
  onDragEnd,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  const suppressClickRef = useRef(false);
  const hasNoJobs = (lead.statusJobs?.open ?? 0) === 0 || (lead.statusJobs?.exp ?? 0) > 0;
  const managerName = lead.expand?.userId?.name ?? lead.userId ?? 'Без менеджера';
  const managerEmail = lead.expand?.userId?.email;
  const managerColorKey = lead.expand?.userId?.id ?? lead.userId ?? managerName;
  const initials = getManagerInitials(managerName, managerEmail);
  const color = getManagerColor(managerColorKey);
  const title = lead.title?.trim() || 'Без названия';
  const taskNumber = `#${lead.id.slice(-4)}`;
  const sending = formatDate(lead.sending ?? '');
  const depLeg = formatTripLeg(lead.sending);
  const renderLeg = (city: string, label: string, leg: NonNullable<ReturnType<typeof formatTripLeg>>) => (
    <div className="card-trip">
      {city && <div className="card-trip-city">{city}</div>}
      <div className="card-trip-line">{label} {leg.date} в {leg.time}</div>
      <div className="card-trip-msk">по мск {leg.mskTime}</div>
    </div>
  );

  const docPresent = presentDocTypes(lead.types);

  return (
    <div
      className={`card${hasNoJobs ? ' card-no-jobs' : ''}${isDragging ? ' card-dragging' : ''}${selected ? ' card-selected' : ''}`}
      draggable={!selectable}
      onDragStart={event => {
        if (selectable) return;
        suppressClickRef.current = true;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', lead.id);
        onDragStart?.(lead.id);
      }}
      onDragEnd={() => {
        window.setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        onDragEnd?.();
      }}
      onClick={() => {
        if (selectable) {
          onToggleSelect?.(lead.id);
          return;
        }
        if (suppressClickRef.current) return;
        onClick(lead.id, lead.title);
      }}
    >
      <div className="card-top">
        {selectable && (
          <span className={`card-check${selected ? ' checked' : ''}`}>
            {selected && <IconCheck size={13} stroke={3} />}
          </span>
        )}
        <div className="card-name">{title}</div>
        <div className="card-id">{taskNumber}</div>
      </div>
      {(lead.expand?.tags?.length ?? 0) > 0 && (
        <div className="card-tags">
          {lead.expand!.tags!.map(tag => {
            const c = tag.colors;
            return (
              <span
                key={tag.id}
                className="card-tag"
                style={c ? { background: c.bg, color: c.text, borderColor: c.dot + '66' } : undefined}
              >
                <span className="card-tag-dot" style={{ background: c?.dot ?? 'var(--text3)' }} />
                {tag.name}
              </span>
            );
          })}
        </div>
      )}
      {sending && (
        <div className="card-row">
          <div className="card-lbl">Отправка</div>
          <div className="card-val card-val-with-icon">
            <IconCalendarEvent size={14} stroke={1.8} />
            <span>{sending}</span>
          </div>
        </div>
      )}
      {lead.arrival && (
        <div className="card-row">
          <div className="card-lbl">Приезд</div>
          <div className="card-val card-val-with-icon">
            <IconPlaneArrival size={14} stroke={1.8} />
            <span>{formatDate(lead.arrival)}</span>
          </div>
        </div>
      )}
      {depLeg && (
        <div className="card-trips">
          {renderLeg(lead.destination?.trim() || '', 'выезд', depLeg)}
        </div>
      )}
      <div className="card-docs">
        {DOC_ICONS.map(({ type, label, Icon }) => {
          const ready = docPresent.has(type);
          return (
            <span
              key={type}
              className={`card-doc${ready ? ' ready' : ' missing'}`}
              title={`${label}: ${ready ? 'есть' : 'нет'}`}
            >
              <Icon size={14} stroke={2} />
            </span>
          );
        })}
        <span
          className={`card-doc${lead.orderReceived ? ' ready' : ' missing'}`}
          title={`Предписание: ${lead.orderReceived ? 'получено' : 'не получено'}`}
        >
          {lead.orderReceived
            ? <IconClipboardCheck size={14} stroke={2} />
            : <IconClipboardX size={14} stroke={2} />}
        </span>
      </div>
      <div className="card-div" />
      <div className="card-foot">
        <div className="card-mgr">
          <div className="mgr-av" style={{background: color}}>{initials}</div>
          <div className="mgr-nm">{managerName}</div>
        </div>
        <div className="card-mgr" style={{flexDirection: 'column', gap: 0}}>
          <div className="card-date">{formatDate(lead.created)}</div>
          <div className="card-date">{formatDate(lead.updated)}</div>
        </div>
        </div>
      </div>
      );
      };

      export {LeadCard};
